import { useCallback, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { Image, MediaJob, Video } from "@/features/studio-ai/types/studio-ai.types"

import { Download, ImageIcon, Pencil, Search, Trash2, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { MediaPreviewCard } from "@/features/studio-ai/components/media-preview-card"
import { useImageModal } from "@/features/studio-ai/hooks/use-image-modal"
import { useStudioImageThumbnail } from "@/features/studio-ai/hooks/use-studio-image-thumbnail"
import { useVideoPlayback } from "@/features/studio-ai/hooks/use-video-playback"
import { MediaType } from "@/features/studio-ai/types/studio-ai.types"
import { cn } from "@/lib/utils"
import { ImageCacheService } from "@/services/image-cache-service"
import { useStudioAIStore } from "@/stores/studio-ai-store"

type StudioAIGalleryTabsProps = {
  jobs: MediaJob[]
  isLoading: boolean
  onRefresh: () => void
}

function sortByRecent(jobs: MediaJob[]): MediaJob[] {
  return [...jobs].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
    return dateB - dateA
  })
}

/**
 * <img> wrapper for an image inside the modal that goes through the
 * batch-prefetch cache. Falls back to ImageIcon when the image isn't
 * loadable yet (in-flight, failed, or unknown id). Avoids replicating
 * the cache+legacy lookup inline at the call sites.
 */
function ModalImage({
  jobId,
  legacyUrl,
  alt,
  className,
  fallbackIcon,
}: {
  jobId: string | undefined
  legacyUrl: string | null | undefined
  alt: string
  className: string
  fallbackIcon: React.ReactNode
}) {
  const { url, error, onImgError } = useStudioImageThumbnail(jobId, legacyUrl)
  if (!url || error) {
    return (
      <div className="bg-muted flex h-full w-full items-center justify-center">{fallbackIcon}</div>
    )
  }
  return <img alt={alt} className={className} src={url} onError={onImgError} />
}

export function StudioAIGalleryTabs({ jobs, isLoading, onRefresh }: StudioAIGalleryTabsProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi" })
  const [searchQuery, setSearchQuery] = useState("")
  const setEditorImageUrl = useStudioAIStore((state) => state.setEditorImageUrl)
  const setEditorImageJobId = useStudioAIStore((state) => state.setEditorImageJobId)
  const setEditorSourceJobId = useStudioAIStore((state) => state.setEditorSourceJobId)
  const setCurrentView = useStudioAIStore((state) => state.setCurrentView)

  const {
    selectedVideo,
    isPlayerOpen,
    videoBlob,
    isLoadingVideo,
    handlePlay,
    handlePlayerOpenChange,
    handleDownloadVideo,
    handleDeleteVideo,
  } = useVideoPlayback({ onRefresh })

  const {
    selectedImage,
    setSelectedImage,
    modalOriginal,
    modalEdits,
    isImageModalOpen,
    handleView,
    handleImageModalOpenChange,
    handleDownloadImage,
    handleDeleteImage,
  } = useImageModal({ onRefresh })

  const filteredJobs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return jobs

    return jobs.filter((job) => {
      return job.prompt.toLowerCase().includes(query)
    })
  }, [jobs, searchQuery])

  const videoJobs = useMemo(
    () =>
      sortByRecent(
        filteredJobs.filter((job) => !job.media_type || job.media_type === MediaType.Video)
      ),
    [filteredJobs]
  )

  const recentJobs = useMemo(() => sortByRecent(filteredJobs).slice(0, 4), [filteredJobs])

  const imageJobs = useMemo(() => {
    const images = sortByRecent(
      filteredJobs.filter(
        (job) => job.media_type === MediaType.Image && (job as Image).edit_mode !== "edit"
      )
    )
    const editGroups = new Map<string, Image[]>()
    for (const job of filteredJobs) {
      const img = job as Image
      if (img.source_job_id) {
        const group = editGroups.get(img.source_job_id) || []
        group.push(img)
        editGroups.set(img.source_job_id, group)
      }
    }
    return images.map((job) => {
      const edits = editGroups.get(job.job_id)
      if (edits && edits.length > 0) {
        return { ...job, _editCount: edits.length, _edits: sortByRecent(edits) as Image[] }
      }
      return job
    })
  }, [filteredJobs])

  const handleModalEditImage = useCallback(
    (image: Image) => {
      const imageUrl = image.image_url || ImageCacheService.getImage(image.job_id)
      if (imageUrl) {
        setEditorImageUrl(imageUrl)
        setEditorImageJobId(image.job_id)
        setEditorSourceJobId(image.source_job_id || image.job_id)
        setCurrentView("image-editor")
        handleImageModalOpenChange(false)
      }
    },
    [
      setEditorImageUrl,
      setEditorImageJobId,
      setEditorSourceJobId,
      setCurrentView,
      handleImageModalOpenChange,
    ]
  )

  const renderJobGrid = (jobList: MediaJob[], emptyKey: string) => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Spinner className="text-primary mx-auto mb-3 size-10" />
          <p className="text-muted-foreground text-sm">{t("gallery.loading")}</p>
        </div>
      )
    }

    if (jobList.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ImageIcon className="text-muted-foreground/40 mb-3 size-10" />
          <p className="text-muted-foreground text-sm">
            {searchQuery ? t("landing.noMatches") : t(emptyKey)}
          </p>
        </div>
      )
    }

    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {jobList.map((job) => {
          const isVideo = !job.media_type || job.media_type === MediaType.Video
          const editCount = (job as MediaJob & { _editCount?: number })._editCount
          if (isVideo) {
            return (
              <MediaPreviewCard
                key={job.job_id}
                editCount={editCount}
                media={job as Video}
                mediaType={MediaType.Video}
                onDownload={() => handleDownloadVideo(job as Video)}
                onPlay={() => handlePlay(job as Video)}
              />
            )
          }
          return (
            <MediaPreviewCard
              key={job.job_id}
              editCount={editCount}
              media={job as Image}
              mediaType={MediaType.Image}
              onDownload={() => handleDownloadImage(job as Image)}
              onView={() => handleView(job as Image)}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            className="h-9 pr-8 pl-9"
            placeholder={t("landing.searchPlaceholder")}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className={cn(
                "absolute top-1/2 right-2.5 -translate-y-1/2",
                "text-muted-foreground hover:text-foreground"
              )}
              onClick={() => setSearchQuery("")}
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {recentJobs.length > 0 && (
        <section className="mb-8">
          <h3 className="mb-4 text-lg font-semibold">{t("landing.sections.recent")}</h3>
          {renderJobGrid(recentJobs, "landing.emptyPersonal")}
        </section>
      )}

      {videoJobs.length > 0 && (
        <section className="mb-8">
          <h3 className="mb-4 text-lg font-semibold">{t("landing.sections.videos")}</h3>
          {renderJobGrid(videoJobs, "landing.emptyPersonal")}
        </section>
      )}

      {imageJobs.length > 0 && (
        <section className="mb-8">
          <h3 className="mb-4 text-lg font-semibold">{t("landing.sections.images")}</h3>
          {renderJobGrid(imageJobs, "landing.emptyPersonal")}
        </section>
      )}

      {videoJobs.length === 0 && imageJobs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ImageIcon className="text-muted-foreground/40 mb-3 size-10" />
          <p className="text-muted-foreground text-sm">
            {searchQuery ? t("landing.noMatches") : t("landing.emptyPersonal")}
          </p>
        </div>
      )}

      {/* Video Player Dialog */}
      <Dialog open={isPlayerOpen} onOpenChange={handlePlayerOpenChange}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-4xl">
          <DialogTitle className="sr-only">{selectedVideo?.prompt}</DialogTitle>
          {selectedVideo && (
            <div className="flex flex-col gap-4">
              {/* Video Player */}
              {isLoadingVideo && (
                <div className="flex aspect-video items-center justify-center">
                  <Spinner className="text-muted-foreground h-12 w-12" />
                </div>
              )}
              {!isLoadingVideo && videoBlob && (
                <div className="aspect-video">
                  <video controls className="h-full w-full rounded-lg" src={videoBlob}>
                    <track kind="captions" />
                  </video>
                </div>
              )}

              {/* Prompt */}
              <p className="text-muted-foreground text-sm">{selectedVideo.prompt}</p>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void handleDownloadVideo(selectedVideo)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {t("mediaCard.downloadVideo")}
                </Button>
                <Button
                  className="text-destructive hover:text-destructive"
                  size="sm"
                  variant="outline"
                  onClick={() => void handleDeleteVideo(selectedVideo)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("mediaCard.deleteVideo")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Modal */}
      <Dialog open={isImageModalOpen} onOpenChange={handleImageModalOpenChange}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-4xl">
          <DialogTitle className="sr-only">{selectedImage?.prompt}</DialogTitle>
          {selectedImage && (
            <div className="flex flex-col gap-4">
              {/* Main Image */}
              <div className="flex justify-center">
                <ModalImage
                  alt={selectedImage.prompt}
                  className="max-h-[60vh] w-auto rounded-lg object-contain"
                  jobId={selectedImage.job_id}
                  fallbackIcon={
                    <div className="text-center">
                      <ImageIcon className="text-muted-foreground mx-auto mb-2 h-16 w-16" />
                      <p className="text-muted-foreground max-w-md text-sm">
                        {selectedImage.prompt}
                      </p>
                    </div>
                  }
                  legacyUrl={
                    selectedImage.image_url || ImageCacheService.getImage(selectedImage.job_id)
                  }
                />
              </div>

              {/* Prompt */}
              <p className="text-muted-foreground text-sm">{selectedImage.prompt}</p>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownloadImage(selectedImage)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {t("mediaCard.downloadImage")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleModalEditImage(selectedImage)}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("mediaCard.editImage")}
                </Button>
                <Button
                  className="text-destructive hover:text-destructive"
                  size="sm"
                  variant="outline"
                  onClick={() => void handleDeleteImage(selectedImage)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("mediaCard.deleteImage")}
                </Button>
              </div>

              {/* Edits Strip */}
              {modalEdits.length > 0 && (
                <div>
                  <p className="text-muted-foreground mb-2 text-xs font-medium">
                    {modalEdits.length} {modalEdits.length === 1 ? "edit" : "edits"}
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {/* Original thumbnail */}
                    <button
                      type="button"
                      className={cn(
                        "h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border-2 transition-colors",
                        selectedImage.job_id === modalOriginal?.job_id
                          ? "border-primary"
                          : "hover:border-muted-foreground/30 border-transparent"
                      )}
                      onClick={() => modalOriginal && setSelectedImage(modalOriginal)}
                    >
                      <ModalImage
                        alt="Original"
                        className="h-full w-full object-cover"
                        fallbackIcon={<ImageIcon className="text-muted-foreground h-4 w-4" />}
                        jobId={modalOriginal?.job_id}
                        legacyUrl={
                          modalOriginal?.image_url ||
                          ImageCacheService.getImage(modalOriginal?.job_id || "")
                        }
                      />
                    </button>
                    {/* Edit thumbnails */}
                    {modalEdits.map((edit) => (
                      <button
                        key={edit.job_id}
                        type="button"
                        className={cn(
                          "h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border-2 transition-colors",
                          selectedImage.job_id === edit.job_id
                            ? "border-primary"
                            : "hover:border-muted-foreground/30 border-transparent"
                        )}
                        onClick={() => setSelectedImage(edit)}
                      >
                        <ModalImage
                          alt={edit.prompt}
                          className="h-full w-full object-cover"
                          fallbackIcon={<Pencil className="text-muted-foreground h-4 w-4" />}
                          jobId={edit.job_id}
                          legacyUrl={edit.image_url || ImageCacheService.getImage(edit.job_id)}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
